import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import type { SortingState } from "@tanstack/react-table";
import { Plus } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
      DashboardControlGroup,
      DashboardControlRow,
} from "@/components/patterns/content-zone";
import {
      DashboardPageActions,
      DashboardPageHeader,
      DashboardPageShell,
} from "@/components/patterns/headers";
import { AddCompetitorDialog } from "@/components/competitors-table/add-competitor-dialog";
import { EditCompetitorDialog } from "@/components/competitors-table/edit-competitor-dialog";
import { CompetitorsTable } from "@/components/competitors-table/table";
import {
      COMPETITORS_PAGE_SIZE,
      DEFAULT_COMPETITOR_SORTING,
      createCompetitorSortingChangeHandler,
      getCompetitorSortParams,
} from "@/components/competitors-table/table-state";
import { Button } from "@/components/ui/button";
import { BrandDisplayPeriodControl } from "@/components/brand-display-period-control";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { usePaginate } from "@/hooks/use-paginate";
import { getDisplayPeriodQuery } from "@/lib/display-period";
import { useTRPC } from "@/lib/trpc";
import { useUserPreferencesStore } from "@/state/store/userPreferences";
import type { CompetitorWithMetrics } from "@/types/data";

export const Route = createFileRoute("/_dashboard/$brand/competitors")({
      component: CompetitorsPage,
});

function CompetitorsPage() {
      const trpc = useTRPC();
      const queryClient = useQueryClient();
      const { brand } = Route.useParams();
      const { data: brandObject } = useQuery(trpc.brands.getBrandBySlug.queryOptions({ slug: brand }));
      const brandId = brandObject?.id;
      const [activeTab, setActiveTab] = useState<"competitors" | "ignored">("competitors");

      const defaultBrandDashboardPeriod = useUserPreferencesStore((state) => state.defaultBrandDashboardPeriod);
      const displayPeriodQuery = getDisplayPeriodQuery(defaultBrandDashboardPeriod);

      const [activeSorting, setActiveSorting] = useState<SortingState>(DEFAULT_COMPETITOR_SORTING);
      const [ignoredSorting, setIgnoredSorting] = useState<SortingState>(DEFAULT_COMPETITOR_SORTING);
      const { pagination: activePagination, setPagination: setActivePagination, skip: activeSkip, take: activeTake } =
            usePaginate(COMPETITORS_PAGE_SIZE);
      const {
            pagination: ignoredPagination,
            setPagination: setIgnoredPagination,
            skip: ignoredSkip,
            take: ignoredTake,
      } = usePaginate(COMPETITORS_PAGE_SIZE);

      const activeSortParams = getCompetitorSortParams(activeSorting);
      const ignoredSortParams = getCompetitorSortParams(ignoredSorting);

      const { data: activeCompetitors, isLoading } = useQuery(
            trpc.competitors.getPaginatedCompetitorsWithMetrics.queryOptions(
                  {
                        parentBrandId: brandId!,
                        ...displayPeriodQuery,
                        skip: activeSkip,
                        take: activeTake,
                        sortBy: activeSortParams.sortBy,
                        sortOrder: activeSortParams.sortOrder,
                  },
                  {
                        enabled: !!brandId,
                        placeholderData: keepPreviousData,
                  },
            ),
      );
      const { data: ignoredCompetitors, isLoading: isIgnoredLoading } = useQuery(
            trpc.competitors.getPaginatedIgnoredCompetitorsWithMetrics.queryOptions(
                  {
                        parentBrandId: brandId!,
                        ...displayPeriodQuery,
                        skip: ignoredSkip,
                        take: ignoredTake,
                        sortBy: ignoredSortParams.sortBy,
                        sortOrder: ignoredSortParams.sortOrder,
                  },
                  {
                        enabled: !!brandId,
                        placeholderData: keepPreviousData,
                  },
            ),
      );

      const activePageItems = activeCompetitors?.items ?? [];
      const hasUnpinnedCompetitors = activePageItems.some((competitor) => !competitor.pinned);

      useEffect(() => {
            if (!activeCompetitors) return;

            const lastPageIndex = Math.max(0, activeCompetitors.totalPages - 1);
            if (activePagination.pageIndex > lastPageIndex) {
                  setActivePagination((prev) => ({ ...prev, pageIndex: lastPageIndex }));
            }
      }, [activeCompetitors, activePagination.pageIndex, setActivePagination]);

      useEffect(() => {
            if (!ignoredCompetitors) return;

            const lastPageIndex = Math.max(0, ignoredCompetitors.totalPages - 1);
            if (ignoredPagination.pageIndex > lastPageIndex) {
                  setIgnoredPagination((prev) => ({ ...prev, pageIndex: lastPageIndex }));
            }
      }, [ignoredCompetitors, ignoredPagination.pageIndex, setIgnoredPagination]);

      const invalidateCompetitorQueries = useCallback(async () => {
            await Promise.all([
                  queryClient.invalidateQueries({
                        queryKey: trpc.competitors.getPaginatedCompetitorsWithMetrics.queryKey(),
                  }),
                  queryClient.invalidateQueries({
                        queryKey: trpc.competitors.getPaginatedIgnoredCompetitorsWithMetrics.queryKey(),
                  }),
                  queryClient.invalidateQueries({
                        queryKey: trpc.competitors.getCompetitorsWithMetrics.queryKey(),
                  }),
                  queryClient.invalidateQueries({
                        queryKey: trpc.competitors.getIgnoredCompetitorsWithMetrics.queryKey(),
                  }),
            ]);
      }, [queryClient, trpc]);

      const updateTop5OrderMutation = useMutation(trpc.competitors.updateTop5Order.mutationOptions());

      const updateTop5Order = useCallback(
            (sortedCompetitors: CompetitorWithMetrics[]) => {
                  if (!brandId || sortedCompetitors.length === 0) return;

                  const top5Ids = sortedCompetitors.slice(0, 5).map((competitor) => competitor.competitorProfileId);
                  updateTop5OrderMutation.mutate({
                        parentBrandId: brandId,
                        top5Ids,
                  });
            },
            [brandId, updateTop5OrderMutation],
      );

      const togglePinMutation = useMutation(
            trpc.competitors.toggleCompetitorPin.mutationOptions({
                  onError: (error) => {
                        toast.error(error.message || "Failed to toggle pin");
                  },
                  onSettled: () => {
                        void invalidateCompetitorQueries();
                  },
            }),
      );

      const setIgnoredMutation = useMutation(
            trpc.competitors.setCompetitorIgnored.mutationOptions({
                  onSuccess: (_data, variables) => {
                        toast.success(variables.ignored ? "Competitor ignored" : "Competitor restored");
                  },
                  onError: (error) => {
                        toast.error(error.message || "Failed to update competitor");
                  },
                  onSettled: () => {
                        void invalidateCompetitorQueries();
                  },
            }),
      );

      const ignoreUnpinnedMutation = useMutation(
            trpc.competitors.ignoreAllUnpinnedCompetitors.mutationOptions({
                  onSuccess: () => {
                        toast.success("Unpinned competitors ignored");
                  },
                  onError: (error) => {
                        toast.error(error.message || "Failed to ignore unpinned competitors");
                  },
                  onSettled: () => {
                        void invalidateCompetitorQueries();
                  },
            }),
      );

      const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
      const addMutation = useMutation(
            trpc.competitors.createCompetitor.mutationOptions({
                  onSuccess: () => {
                        void invalidateCompetitorQueries();
                        toast.success("Competitor added");
                        setIsAddDialogOpen(false);
                  },
                  onError: (error) => {
                        toast.error(error.message || "Failed to add competitor");
                  },
            }),
      );

      const [editingCompetitor, setEditingCompetitor] = useState<CompetitorWithMetrics | null>(null);
      const editMutation = useMutation(
            trpc.competitors.updateCompetitor.mutationOptions({
                  onSuccess: () => {
                        void invalidateCompetitorQueries();
                        toast.success("Competitor updated");
                        setEditingCompetitor(null);
                  },
                  onError: (error) => {
                        toast.error(error.message || "Failed to update competitor");
                  },
            }),
      );

      const handleTogglePin = (competitor: CompetitorWithMetrics) => {
            togglePinMutation.mutate({
                  competitorProfileId: competitor.competitorProfileId,
                  pinned: !competitor.pinned,
            });
      };

      const handleSetIgnored = (competitor: CompetitorWithMetrics, ignored: boolean) => {
            setIgnoredMutation.mutate({
                  competitorProfileId: competitor.competitorProfileId,
                  ignored,
            });
      };

      const handleIgnoreUnpinned = () => {
            if (!brandId) return;

            ignoreUnpinnedMutation.mutate({
                  parentBrandId: brandId,
            });
      };

      const handleEdit = (competitor: CompetitorWithMetrics) => {
            setEditingCompetitor(competitor);
      };

      const handleSaveEdit = (competitorBrandId: string, name: string, website: string) => {
            editMutation.mutate({ brandId: competitorBrandId, name, website });
      };

      const handleAddCompetitor = (name: string, website: string) => {
            if (!brandId) return;

            addMutation.mutate({
                  parentBrandId: brandId,
                  name,
                  website,
            });
      };

      const handleActiveSortingChange = createCompetitorSortingChangeHandler({
            setPagination: setActivePagination,
            setSorting: setActiveSorting,
      });
      const handleIgnoredSortingChange = createCompetitorSortingChangeHandler({
            setPagination: setIgnoredPagination,
            setSorting: setIgnoredSorting,
      });

      return (
            <DashboardPageShell as="div" contentClassName="space-y-6">
                  <DashboardPageHeader
                        title="Competitors"
                        description="Top 5 competitors are shown in charts. Pin competitors to prioritize them."
                        aside={
                              <DashboardPageActions>
                                    <Button
                                          variant="outline"
                                          className="bg-card"
                                          onClick={handleIgnoreUnpinned}
                                          disabled={
                                                activeTab !== "competitors" ||
                                                !hasUnpinnedCompetitors ||
                                                ignoreUnpinnedMutation.isPending
                                          }
                                    >
                                          Ignore unpinned competitors
                                    </Button>
                                    <Button onClick={() => setIsAddDialogOpen(true)} className="gap-2">
                                          <Plus className="h-4 w-4" />
                                          Add Competitor
                                    </Button>
                              </DashboardPageActions>
                        }
                  />
                  <Tabs
                        value={activeTab}
                        onValueChange={(value) => setActiveTab(value as "competitors" | "ignored")}
                        className="gap-4"
                  >
                        <DashboardControlRow>
                              <TabsList>
                                    <TabsTrigger value="competitors" className="data-[state=active]:bg-card">
                                          Competitors
                                    </TabsTrigger>
                                    <TabsTrigger value="ignored" className="data-[state=active]:bg-card">
                                          Ignored
                                    </TabsTrigger>
                              </TabsList>
                              <DashboardControlGroup className="md:justify-end">
                                    <BrandDisplayPeriodControl className="md:justify-end" />
                              </DashboardControlGroup>
                        </DashboardControlRow>
                        <TabsContent value="competitors" className="mt-0">
                              <CompetitorsTable
                                    mode="active"
                                    competitors={activePageItems}
                                    totalCount={activeCompetitors?.totalCount ?? 0}
                                    isLoading={isLoading}
                                    sorting={activeSorting}
                                    onSortingChange={handleActiveSortingChange}
                                    pagination={activePagination}
                                    onPaginationChange={setActivePagination}
                                    resultPageIndex={activeCompetitors?.pageIndex}
                                    resultPageSize={activeCompetitors?.pageSize}
                                    handleTogglePin={handleTogglePin}
                                    handleSetIgnored={handleSetIgnored}
                                    handleEdit={handleEdit}
                                    onOrderChange={updateTop5Order}
                              />
                        </TabsContent>
                        <TabsContent value="ignored" className="mt-0">
                              <CompetitorsTable
                                    mode="ignored"
                                    competitors={ignoredCompetitors?.items ?? []}
                                    totalCount={ignoredCompetitors?.totalCount ?? 0}
                                    isLoading={isIgnoredLoading}
                                    sorting={ignoredSorting}
                                    onSortingChange={handleIgnoredSortingChange}
                                    pagination={ignoredPagination}
                                    onPaginationChange={setIgnoredPagination}
                                    resultPageIndex={ignoredCompetitors?.pageIndex}
                                    resultPageSize={ignoredCompetitors?.pageSize}
                                    handleTogglePin={handleTogglePin}
                                    handleSetIgnored={handleSetIgnored}
                                    handleEdit={handleEdit}
                                    onOrderChange={updateTop5Order}
                              />
                        </TabsContent>
                  </Tabs>
                  <EditCompetitorDialog
                        competitor={editingCompetitor}
                        open={!!editingCompetitor}
                        onOpenChange={(open) => !open && setEditingCompetitor(null)}
                        onSave={handleSaveEdit}
                        isPending={editMutation.isPending}
                  />
                  <AddCompetitorDialog
                        open={isAddDialogOpen}
                        onOpenChange={setIsAddDialogOpen}
                        onAdd={handleAddCompetitor}
                        isPending={addMutation.isPending}
                  />
            </DashboardPageShell>
      );
}
